import { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  getOrdersToPickup,
  confirmReturnedToPickupPoint,
} from '../../../services';

import { ORDER_STATUSES, ORDER_TYPES } from '@wareclouds/frontend-common/b2c';
import { ORDER_TYPE_PICKUP_AT_WARECLOUD } from '../../../../../config/consts/orders.js';

import useRefreshOrder from '../../../../../hooks/useRefreshOrders.js';
import getEmojiTier from '../../../../../utils/getEmojiTier.js';
import { handleAxiosErrorMessage } from '../../../../../utils';

import {
  setOrdersModalConfig,
  setOrdersToRetrieveFilters,
} from '../../../orderSlice.js';

import FilterSelect from '../../../../common/_inputs/FilterSelect.js';
import OrderCard from '../../../OrderCard.js';
import useSetRetrieveCounter from '../hooks/useSetRetrieveCounter.js';
import useHandleOrdersFetching from '../hooks/useHandleOrdersFetching.js';
import useObserveBottomCard from '../hooks/useObserveBottomCard.js';
import useObserveTopCard from '../hooks/useObserveTopCard.js';
import EmptyStateReceiveOrders from '../components/toReceiveDefault/EmptyStateReceiveOrders.js';
import ReceiveOrderQRScannerViewer from '../components/toReceiveDefault/ReceiveOrderQRScannerViewer.js';
import ReceiveOrderBarcodeScannerViewer from '../components/toReceiveDefault/ReceiveOrderBarcodeScannerViewer.js';
import { SmallSpinner, QRButton, useWLoading } from '../../../../common';
import { NOTIFICATION_TYPES } from '../../../../../config/consts/commons.js';
import { setMessage } from '../../../../toasts/toastSlice.js';
import { useToReceiveFromWarecloud } from '../../application/hooks';

const ToReceiveDefaultView = () => {
  const PICKUP_BUTTON_STATUS = {
    SUBMITTING: 'submitting',
    AWAITING_CONFIRMATION: 'awaiting_confirmation',
  };
  const { t } = useTranslation(['description', 'button', 'system', 'glossary']);
  const dispatch = useDispatch();
  const { handleClose } = useWLoading();
  const [showQRScan, setShowQRScan] = useState(false);
  const [showBarcodeScan, setShowBarcodeScan] = useState(false);
  const [pickupOrderStatusById, setPickupOrderStatusById] = useState({});
  const {
    wareclouds,
    ordersToRetrieveFilters: filters,
    ordersToRetrieveOrderIndex: index,
  } = useSelector((state) => state.orders);

  const [
    {
      isLoading,
      orders: ordersToRetrieve,
      totalPages,
      bottomInView,
      topInView,
      count,
    },
  ] = useRefreshOrder({
    apiCall: getOrdersToPickup,
    filters,
  });

  useSetRetrieveCounter(count);

  const hasExpressStorePickupOrders = useMemo(() => {
    return ordersToRetrieve.some(
      (order) => order.id_order_type === ORDER_TYPES.EXPRESS
    );
  }, [ordersToRetrieve]);

  const {
    orders,
    loadMoreBottomRef,
    loadMoreTopRef,
    isLoadingMore,
    ordersLoading,
    setIsLoadingMore,
    handleUpdateOrders,
  } = useHandleOrdersFetching({ ordersToRetrieve, isLoading, index });
  const { mutate: postReceiveFromWarecloud } = useToReceiveFromWarecloud();

  useObserveBottomCard({
    filters,
    bottomInView,
    loadMoreBottomRef,
    setIsLoadingMore,
    totalPages,
  });

  useObserveTopCard({
    filters,
    topInView,
    totalPages,
    loadMoreTopRef,
    setIsLoadingMore,
  });

  const toggleShowMoreInfo = (payload) => {
    const ordersCopy = [...orders];

    ordersCopy[payload].showMore = ordersCopy[payload].showMore ? false : true;

    handleUpdateOrders(ordersCopy);
  };

  const handleRetrieveOrderPrompt = ({ order }) => {
    if (
      order.id_order_type === ORDER_TYPE_PICKUP_AT_WARECLOUD &&
      order.can_pickup
    ) {
      const orderStatus = pickupOrderStatusById[order.id_order];
      if (
        orderStatus === PICKUP_BUTTON_STATUS.SUBMITTING ||
        orderStatus === PICKUP_BUTTON_STATUS.AWAITING_CONFIRMATION
      ) {
        return;
      }

      setPickupOrderStatusById((prev) => ({
        ...prev,
        [order.id_order]: PICKUP_BUTTON_STATUS.SUBMITTING,
      }));
      confirmReturnedToPickupPoint({ id_order: order.id_order })
        .then(() => {
          setPickupOrderStatusById((prev) => ({
            ...prev,
            [order.id_order]: PICKUP_BUTTON_STATUS.AWAITING_CONFIRMATION,
          }));
          dispatch(setOrdersToRetrieveFilters({ ...filters }));
          dispatch(
            setMessage({
              body: t('ORDER_WITHDRAW_SUCCESS', {
                ns: 'system',
                order: order.order_code,
              }),
              type: NOTIFICATION_TYPES.SUCCESS,
            })
          );
        })
        .catch((err) => {
          setPickupOrderStatusById((prev) => {
            const next = { ...prev };
            delete next[order.id_order];
            return next;
          });
          const errorMessage = handleAxiosErrorMessage(err);
          dispatch(
            setMessage({
              body: t(errorMessage, { ns: 'system' }),
              type: NOTIFICATION_TYPES.ERROR,
            })
          );
        });
      return;
    }

    dispatch(
      setOrdersModalConfig({
        titleOpening: t('orders.retrieve.modal.wish_pickup_order', {
          ns: 'description',
        }),
        show: true,
        order,
        modalType: 'toRetrieve',
      })
    );
  };

  const validateCodeAndConfirmOrder = (code, order) => {
    if (order.scan_value !== code) {
      dispatch(
        setMessage({
          body: t('orders.retrieve.modal.code_error_with_value', {
            ns: 'description',
            code: code,
          }),
          type: NOTIFICATION_TYPES.ERROR,
        })
      );
      handleClose();
      return;
    }

    const payload = {
      id_order: order.id_order,
      order_code: order.order_code,
      comment: '',
      barcode: true,
    };

    postReceiveFromWarecloud(payload, { onSettled: () => handleClose() });
  };

  const getPickupStatusByOrder = (orderId) => {
    const orderStatus = pickupOrderStatusById[orderId];
    return {
      isSubmittingPickup: orderStatus === PICKUP_BUTTON_STATUS.SUBMITTING,
      isAwaitingPickupConfirmation:
        orderStatus === PICKUP_BUTTON_STATUS.AWAITING_CONFIRMATION,
    };
  };

  return (
    <>
      {!hasExpressStorePickupOrders && (
        <QRButton
          variant="contained"
          label="Escanear código"
          onClick={() => setShowBarcodeScan(true)}
          disabled={ordersToRetrieve.length === 0 || isLoading}
          style={{ marginTop: 8 }}
        />
      )}
      <Box sx={{ mb: 2 }}>
        <FilterSelect
          name="id_warecloud"
          handleChange={(e) => {
            const { id_warecloud, ...rest } = filters;
            dispatch(
              setOrdersToRetrieveFilters({
                id_warecloud: e.target.value,
                ...rest,
              })
            );
          }}
          defaultLabel="Wareclouds"
          currentValue={filters.id_warecloud}
          payload={wareclouds}
        />
      </Box>
      <Box className="cards-container">
        {isLoadingMore && !loadMoreTopRef.current && <SmallSpinner />}
        {orders.length < 1 && (
          <EmptyStateReceiveOrders ordersLoading={ordersLoading} />
        )}
        {orders.map((order, index) => {
          const { isSubmittingPickup, isAwaitingPickupConfirmation } =
            getPickupStatusByOrder(order.id_order);

          return (
            <OrderCard
              view="toRetrieve"
              order={order}
              index={index}
              toggleShowMoreInfo={toggleShowMoreInfo}
              handlePrompt={handleRetrieveOrderPrompt}
              buttonText={t('withdraw', { ns: 'button' })}
              cardUser={{
                label: 'Warecloud',
                value: `${order.warecloud_name} ${getEmojiTier(
                  order.warecloud_id_tier
                )}`,
              }}
              outerAttributes={['warecloud_address', 'warecloud_city']}
              innerAttributes={['warecloud_address_detail', 'warecloud_phone']}
              key={`${index}-${order.order_code}-retrieve`}
              unavailable={
                order.id_status_order ===
                  ORDER_STATUSES.TO_RECEIVE_FROM_ECOMMERCE ||
                order.id_status_order === ORDER_STATUSES.ASSEMBLED ||
                order.id_status_order === ORDER_STATUSES.TO_ASSEMBLE ||
                order.id_status_order ===
                  ORDER_STATUSES.IN_ROUTE_TO_PICKUP_POINT
              }
              isPending={
                order.id_order_type === ORDER_TYPE_PICKUP_AT_WARECLOUD
                  ? isSubmittingPickup ||
                    isAwaitingPickupConfirmation ||
                    (!order.can_pickup && !order.id_order_transfer_confirmation)
                  : !order.id_order_transfer_confirmation
              }
              isSubmitting={isSubmittingPickup}
              pendingButtonText={
                order.id_order_type === ORDER_TYPE_PICKUP_AT_WARECLOUD &&
                isAwaitingPickupConfirmation
                  ? 'Esperando confirmación'
                  : undefined
              }
              validateCodeAndConfirmOrder={validateCodeAndConfirmOrder}
            />
          );
        })}
        {isLoadingMore && !loadMoreBottomRef.current && <SmallSpinner />}
      </Box>

      {showQRScan && (
        <ReceiveOrderQRScannerViewer
          open={showQRScan}
          onClose={() => setShowQRScan(false)}
          handlePrompt={handleRetrieveOrderPrompt}
        />
      )}
      {showBarcodeScan && (
        <ReceiveOrderBarcodeScannerViewer
          open={showBarcodeScan}
          onClose={() => setShowBarcodeScan(false)}
        />
      )}
    </>
  );
};

export default ToReceiveDefaultView;
